import React, { Fragment, useState, useCallback, useContext, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { ThumbsDownMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import { colors } from 'src/theme'
import {
  RejectOnboarding,
  DeclineOnboardingStep,
  FetchRejectionReasonsToDecline,
} from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { DeclineReasonModal } from './DeclineReasonModal'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { CollectedData } from 'src/interfaces/collectedData'
import { CollectedDataStepType } from 'src/constants/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { getUser } from 'src/selectors/user'
import { ContractorDetailsModalContext } from '../ContractorDetailsModal.context'
import { getCollectDataStepTitle } from 'src/utils/collectedData'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { RejectionReason } from 'src/interfaces/rejectionReason'

interface Props {
  collectedData: CollectedData
  contractorDetails: ContractorDetails
  onAction(): void | Promise<void>
}

const DeclineButtonComponent = ({ collectedData, contractorDetails, onAction }: Props) => {
  const dispatch = useDispatch()

  const user = useSelector(getUser)

  const { triggerStatusDelayedContractorRefresh } = useContext(ContractorDetailsModalContext)

  const [isLoading, setIsLoading] = useState(false)

  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const {
    isOpen: isDeclineReasonOpen,
    open: openDeclineReason,
    close: closeDeclineReason,
  } = useModalOpen()

  const {
    isOpen: isSensitiveModalOpen,
    open: openSensitiveModal,
    close: closeSensitiveModal,
  } = useModalOpen()

  const rejectContractor = useCallback(async () => {
    try {
      triggerStatusDelayedContractorRefresh()

      await createAsyncAction(
        dispatch,
        RejectOnboarding.request({ partnerDriverId: contractorDetails.id }),
      )

      await onAction()

      if (isMountedRef.current) {
        showToast('Step is rejected.')
      }
    } catch (err) {
      handleApiError(err)
    }
  }, [dispatch, contractorDetails, onAction, triggerStatusDelayedContractorRefresh])

  const handleDecline = useCallback(async () => {
    if (collectedData.collectDataStep.type === CollectedDataStepType.InPersonMeeting) {
      rejectContractor()
    } else {
      try {
        const response = await createAsyncAction<RejectionReason[], void>(
          dispatch,
          FetchRejectionReasonsToDecline.request(),
        )

        if (response && response.length > 0) {
          openDeclineReason()
        } else {
          openSensitiveModal()
        }
      } catch (err) {
        handleApiError(err)
        openSensitiveModal()
      }
    }
  }, [collectedData, rejectContractor, dispatch, openDeclineReason, openSensitiveModal])

  const handleDeclineStep = useCallback(
    async (reason: RejectionReason | null, onComplete: () => void) => {
      setIsLoading(true)

      try {
        triggerStatusDelayedContractorRefresh()

        await createAsyncAction(
          dispatch,
          DeclineOnboardingStep.request({
            stepId: collectedData.collectDataStep.id,
            partnerDriverId: contractorDetails.id,
            reviewedByEmail: user?.email || '',
            partnerReviewRejectionReasonId: reason?.id,
            declineReason: reason?.name,
          }),
        )

        await onAction()

        if (isMountedRef.current) {
          showToast('Step is sent to recollection.')
        }
      } catch (err) {
        handleApiError(err)
      }

      if (isMountedRef.current) {
        setIsLoading(false)
        onComplete()
      }
    },
    [
      collectedData,
      contractorDetails,
      user?.email,
      dispatch,
      onAction,
      triggerStatusDelayedContractorRefresh,
    ],
  )

  const handleDeclineReasonConfirm = useCallback(
    async (reason: RejectionReason) => {
      await handleDeclineStep(reason, closeDeclineReason)
    },
    [handleDeclineStep, closeDeclineReason],
  )

  const handleSensitiveModalConfirm = useCallback(async () => {
    await handleDeclineStep(null, closeSensitiveModal)
  }, [handleDeclineStep, closeSensitiveModal])

  return (
    <Fragment>
      <Button
        onClick={handleDecline}
        disabled={isLoading}
        fullWidth
        size="small"
        color="red"
        css={[tw`flex items-center justify-center rounded`]}
        endIcon={<ThumbsDownMinor width={20} fill={colors.COLORS_RED_500} />}
      >
        Decline
      </Button>
      <DeclineReasonModal
        isOpen={isDeclineReasonOpen}
        onClose={closeDeclineReason}
        onConfirm={handleDeclineReasonConfirm}
        isLoading={isLoading}
      />
      <SensitiveActionModal
        isOpen={isSensitiveModalOpen}
        title="Recollect step?"
        text={`Are you sure you want to initiate ${getCollectDataStepTitle(
          collectedData,
        )} recollection?`}
        submitLabel="Yes"
        onClose={closeSensitiveModal}
        onSubmit={handleSensitiveModalConfirm}
      />
    </Fragment>
  )
}

export default DeclineButtonComponent
